import CreateIcon from "@mui/icons-material/Create"
import { Box, Card, CardContent, Grid, Typography } from "@mui/material"
import React, { useCallback, useContext, useState } from "react"
import { customPalette } from "../../../../../../../../../../theme"
import { SquareBadge } from "../../../../../../../../../components/badges/SquareBadge"
import { IconTrash } from "../../../../../../../../../components/custom-icons/icon-trash"
import { DeleteDialog } from "../../../../../../../../../components/dialog/DeleteDialog"
import { NonEditableTextField } from "../../../../../../../../../components/inputs/text-input/NonEditableTextField"
import { BSParking } from "../../../../../../../../../core/dto/beem-shot/BSParking/BSParking"
import { ParkingEnum, parkingEnumLabels } from "../../../../../../../../../core/enum/beem-shot/ParkingEnum"
import { BSParkingContext } from "../context/BSParkingContext"

interface IProps {
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  setIsUpdateMode: React.Dispatch<React.SetStateAction<boolean>>
  bsParking: BSParking
}

export function BSParkingCard({ bsParking, setIsOpen, setIsUpdateMode }: Readonly<IProps>): React.JSX.Element {
  const { selectedBSParking, setSelectedBSParking, deleteBSParkingById } = useContext(BSParkingContext)

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)

  const handleOpenUpdateModal: () => void = useCallback(() => {
    setIsOpen(true)
    setIsUpdateMode(true)
    setSelectedBSParking(bsParking)
  }, [bsParking, setIsOpen, setIsUpdateMode, setSelectedBSParking])

  const handleOpenDeleteDialog: () => void = useCallback(() => {
    setSelectedBSParking(bsParking)
    setOpenDeleteDialog(true)
  }, [bsParking, setSelectedBSParking])

  const handleCloseDialog: () => void = useCallback(() => {
    setSelectedBSParking(undefined)
    setOpenDeleteDialog(false)
  }, [setSelectedBSParking])

  const handleDeleteParking: () => void = useCallback(() => {
    if (selectedBSParking) {
      deleteBSParkingById(selectedBSParking.id).then(() => {
        setOpenDeleteDialog(false)
      })
    }
  }, [deleteBSParkingById, selectedBSParking])

  return (
    <>
      <Card
        sx={{
          backgroundColor: customPalette.white,
          borderRadius: 3,
          mb: 3,
          width: "80%",
          boxShadow: "2px 4px 20px 0px rgba(0, 0, 0, 0.05)",
        }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" fontWeight={600}>
              {bsParking.name}
            </Typography>
            <Box
              sx={{
                flexGrow: 1,
                height: "2px",
                backgroundColor: customPalette.lightGray7,
                marginLeft: "8px",
              }}
            />
            <SquareBadge
              onClick={handleOpenUpdateModal}
              icon={<CreateIcon sx={{ color: customPalette.lightBlue2 }} />}
              backgroundColor={customPalette.lightGray7}
              cursor="pointer"
            />

            <SquareBadge
              onClick={handleOpenDeleteDialog}
              icon={<IconTrash sx={{ color: bsParking.assigned ? customPalette.lightGray2 : customPalette.lightBlue2 }} />}
              backgroundColor={bsParking.assigned ? customPalette.lightGray5 : customPalette.lightGray7}
              cursor={bsParking.assigned ? "default" : "pointer"}
              disabled={bsParking.assigned}
              tooltipText={
                bsParking.assigned ? "Vous ne pouvez pas supprimer un parking affecté sur un groupe de niveaux." : undefined
              }
            />
          </Box>

          <Grid container columnSpacing={2} alignItems="center">
            <Grid item xs={6}>
              <NonEditableTextField label="Type de parking" value={parkingEnumLabels[bsParking.typeParking]} />
            </Grid>
            <Grid item xs={6}>
              <NonEditableTextField label="Nombre de places" value={bsParking.nombrePlace} />
            </Grid>
            {bsParking.typeParking !== ParkingEnum.EXTERIEUR && (
              <>
                <Grid item xs={6}>
                  <NonEditableTextField label="Nombre d'ascenseurs" value={bsParking.nombreAscenseur} />
                </Grid>
                <Grid item xs={6}>
                  <NonEditableTextField label="Nombre d'escaliers" value={bsParking.nombreEscalier} />
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      <DeleteDialog
        isOpen={openDeleteDialog}
        id="deleteBSBuildingParking"
        title="Suppression du parking"
        contentText={`Êtes vous sûr de vouloir supprimer le parking ${selectedBSParking?.name} ?`}
        handleClose={handleCloseDialog}
        action={handleDeleteParking}
      />
    </>
  )
}
